import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { useSessionStorage } from '@/hooks/useSessionStorage'
import { ENDPOINTS } from '@/util/endpoints'
import { HubConnectionState, HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import { createContext, useCallback, useContext, useState, ReactNode, useEffect, useRef } from 'react'

const HUB_URL = `${ENDPOINTS.DOTNET}/rest/collaboration-hub`

const RECONNECT_DELAYS = [0, 2000, 5000, 10000, 15000, 30000]

interface ConnectionHubContextType {
  connection: HubConnection
  reconnectAttempt: number
  refreshConnection: () => Promise<void>
}

const ConnectionHubContext = createContext<ConnectionHubContextType | undefined>(undefined)

function createConnection(token: string, onReconnected: () => void): HubConnection {
  const connection = new HubConnectionBuilder()
    .withUrl(HUB_URL, {
      accessTokenFactory: () => token,
      withCredentials: true,
    })
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (retryContext) => {
        const delay = RECONNECT_DELAYS[retryContext.previousRetryCount] ?? 30000
        console.log(`[ConnectionHub] Reconnect attempt ${retryContext.previousRetryCount + 1}, delay: ${delay}ms`)
        return delay
      },
    })
    .configureLogging(LogLevel.Information)
    .build()

  connection.onreconnected((connectionId) => {
    console.log(`[ConnectionHub] Connection reconnected with id: ${connectionId}`)
    onReconnected()
  })
  connection.onreconnecting((error) => {
    console.log(`[ConnectionHub] Connection reconnecting...`, error)
  })
  connection.onclose((error) => {
    console.log(`[ConnectionHub] Connection closed`, error)
  })

  return connection
}

export function ConnectionHubProvider({ children }: { children: ReactNode }) {
  const [token] = useSessionStorage<string>('token', '')
  const [reconnectAttempt, setReconnectAttempt] = useState<number>(0)
  const [isInitialized, setIsInitialized] = useState(false)
  const connectionRef = useRef<HubConnection | null>(null)
  const isMountedRef = useRef(true)

  // Create connection lazily on first access
  const getConnection = useCallback(() => {
    if (!connectionRef.current) {
      connectionRef.current = createConnection(token, () => {
        setReconnectAttempt((prev) => prev + 1)
      })
    }
    return connectionRef.current
  }, [token])

  const refreshConnection = useCallback(async () => {
    const connection = getConnection()
    console.log('[ConnectionHub] Manual refresh requested', {
      connectionState: connection.state,
      reconnectAttempt,
    })

    if (connection.state !== HubConnectionState.Disconnected) {
      console.log('[ConnectionHub] Stopping existing connection')
      try {
        await connection.stop()
        console.log('[ConnectionHub] Connection stopped successfully')
      } catch (err) {
        console.log('[ConnectionHub] Connection stop error (ignored):', err)
      }
    }

    if (isMountedRef.current && connection.state === HubConnectionState.Disconnected) {
      try {
        await connection.start()
        console.log('[ConnectionHub] Connection started successfully')
      } catch (err) {
        if (isMountedRef.current) {
          console.log('[ConnectionHub] Connection start error:', err)
        }
      }
    }
  }, [getConnection, reconnectAttempt])

  useEffect(() => {
    let isCancelled = false
    isMountedRef.current = true
    const connection = getConnection()

    const startConnection = async () => {
      // Wait for any pending stop() to complete (handles StrictMode remount)
      while (connection.state !== HubConnectionState.Disconnected) {
        if (isCancelled) return
        await new Promise((resolve) => setTimeout(resolve, 50))
      }

      if (isCancelled) return

      try {
        await connection.start()
        if (!isCancelled) {
          console.log('[ConnectionHub] Connection started successfully')
          setIsInitialized(true)
        }
      } catch (err) {
        // Ignore AbortError (expected when connection is stopped during negotiation)
        const isAbortError = err instanceof Error && err.name === 'AbortError'
        if (!isCancelled && !isAbortError) {
          console.log('[ConnectionHub] Connection start error:', err)
        }
      }
    }

    startConnection()

    return () => {
      isCancelled = true
      isMountedRef.current = false
      connection.stop()
    }
  }, [getConnection])

  if (!isInitialized || connectionRef.current === null) {
    return <LoadingComponent text="Initializing Hub Connection..." />
  }

  return (
    <ConnectionHubContext.Provider value={{ connection: connectionRef.current, reconnectAttempt, refreshConnection }}>
      {children}
    </ConnectionHubContext.Provider>
  )
}

export function useConnectionHub() {
  const context = useContext(ConnectionHubContext)
  if (context === undefined) {
    throw new Error('useConnectionHub must be used within a ConnectionHubProvider')
  }
  return context
}
